import {computed, Injectable, signal} from '@angular/core';
import {AppComponent} from './app.component';
import {Item} from './item';
import {Adventure} from './adventure';
import {Conditional} from './conditional';
import {Trade} from './trade';
import { LocalizationService } from './localization-service';
import { Place } from './place';

@Injectable({
  providedIn: 'root'
})
export class GameLogicService {
  private adventureData: Adventure;
  public title: string;
  private placeStates: { counter: number, goods?: Item[] }[];
  private counters: Map<string, number>;   // money counter etc.
  private inventory: Set<Item>;   // useful, useless, secret
  public appComponent: AppComponent;
  private moveKey = signal('');

  constructor(private ls: LocalizationService) {
  }

  start(adventureData: Adventure): void {
    this.adventureData = adventureData;
    const ac = this.appComponent;
    this.counters = new Map<string, number>();
    this.counters.set('money', 0);
    this.counters.set('money ever found', 0);
    this.placeStates = [];
    this.inventory = new Set<Item>();
    this.enter(this.adventureData.start.place);
    ac.preDesc.push(this.adventureData.start.intro);
    ac.restartCaption = computed(() => {
      const _ = this.appComponent.languageKey(); // triggers recompute on language change
      return this.adventureData.restart || this.ls._('Restart the adventure');
    });

    // status for the player
    ac.status = computed(() => {
      console.debug(this.appComponent.languageKey(), // triggers recompute on language change
        this.moveKey()); // triggers recompute on place change

      let statusText = '';
      this.adventureData.shown_counters.forEach(key => {
          statusText += this.ls._(key) + ': ' + this.counters.get(key) + '\n';
      });

      this.inventory.forEach(item => {
        if (item.shown_as) {
          statusText += '\n✓ ' + item.shown_as;
        }
      });

      return statusText;
    });

    ac.logicInitialized.set(true);
  }

  enter(target: string): void {
    // update displayed fields from adventure data
    const ac = this.appComponent;
    const here = this.adventureData.places.find(placeObject => placeObject.title === target);
    ac.preDesc = [];
    ac.description = here.description || '';
    ac.postDesc = [];
    ac.actions = [];

    this.interpretPlace(here, ac);

    this.moveKey.set(here.title + ":" + this.placeStates[here.title].counter);
  }

  private interpretPlace(here: Place, ac: AppComponent): void {
    // collect possible actions considering enter condition
    for (const action of here.actions) {
      const there = this.adventureData.places.find(placeObject => placeObject.title === action.place);
      if (!there.enter || this.isMet(there.enter)) {
        ac.actions.push(action);
      }
    }
    ac.actionButtons = ac.actions.filter(a => Boolean(a.caption));

    // maintain visit counter
    if (here.title in this.placeStates) {
      this.placeStates[here.title].counter++;
    } else {
      this.placeStates[here.title] = {counter: 1};
    }
    const now = this.placeStates[here.title].counter;

    // copy value to make available for conditions
    this.counters.set('visit count', now);

    // countdown to death function
    if (here.countdown) {
      //visit_count of 0 = visit_count of Infinity (to support JSON transfers)
      if (here.countdown.visit_count && now >= here.countdown.visit_count) {
        ac.postDesc.push(here.countdown.death);
        ac.actions = [];   // dead hero can't move
        ac.actionButtons = [];
        return;   // block further functions
      }
    }

    // early condition evaluation
    if ('early' in here) {
      for (const customConditional of here.early.values()) {
        if (!this.checkConditional(customConditional, ac)) { return; }
      }
    }

    // finding money function
    if (here.find_money) {
      const moneyConfig = this.adventureData.money;
      if ((now % moneyConfig.visit_count) === 0 && this.counters.get('money ever found') < moneyConfig.piece_count) {
        this.counters.set('money', this.counters.get('money') + 1);
        this.counters.set('money ever found', this.counters.get('money ever found') + 1);
        ac.postDesc.push(moneyConfig.found);
      }
    }

    // buy function
    if (here.poor && this.counters.get('money') <= 0) {
      ac.postDesc.push(here.poor);
    } else if (here.goods) {
      // lazy init field
      if (!('goods' in this.placeStates[here.title])) {
        this.placeStates[here.title].goods = here.goods.slice();
      }

      const stock: Trade[] = this.placeStates[here.title].goods;
      for (let i = 0; i < stock.length; ++i) {
        if (this.isMet(stock[i].if)) {
          const bought = stock.splice(i, 1);
          ac.postDesc.push(bought[0].get);
          this.inventory.add(bought[0].item);
          const price = ('price' in bought[0]) ? this.getValue(bought[0].price) : 1;
          this.counters.set('money', this.counters.get('money') - price);
          break;
        }
      }
    }

    // disorient function
    if (here.disorient) {
      ac.actions.push({
        caption: here.disorient,
        place: ac.actions['' + Math.floor(Math.random() * ac.actions.length)].place
      });
      ac.actionButtons = ac.actions.filter(a => Boolean(a.caption));
    }

    // late custom conditionals
    if ('late' in here) {
      for (const customConditional of here.late.values()) {
        if (!this.checkConditional(customConditional, ac)) { return; }
      }
    }
  
    ac.placeTitle = here.title;
  
    // write copied value back in case Conditionals had side-effects
    this.placeStates[here.title].counter = this.counters.get('visit count');
  }

  private checkConditional(conditional: Conditional, ac: AppComponent): boolean {
    if (conditional) {
      if (this.isMet(conditional.if)) {
        if ('then' in conditional) {
          ac.postDesc.push(conditional.then);
        }

        if ('obtain' in conditional) {
          for (const item of conditional.obtain.values()) {
            this.inventory.add(item);
          }
        }

        if ('lose' in conditional) {
          for (const itemName of conditional.lose.values()) {
            for (const item of this.inventory) {
              if (item.real_name === itemName) {
                this.inventory.delete(item);
                break;
              }
            }
          }
        }

        if ('count' in conditional) {
          for (const count of conditional.count) {
            let value = ('y' in count) ? this.getValue(count.y) : 1;

            if (count.operation === 'add') { value += this.counters.get(count.x); }
            else if (count.operation === 'subtract') { value = this.counters.get(count.x) - value; }
            else if (count.operation === 'max') { value = Math.max(this.counters.get(count.x), value); }
            else if (count.operation === 'min') { value = Math.min(this.counters.get(count.x), value); }

            this.counters.set(count.x, value);
          }
        }

        if ('fate' in conditional) {
          if (conditional.fate === 'kick') {
            const news = ac.postDesc.slice();
            news.unshift(ac.description);
            this.enter(ac.placeTitle);
            while (news.length > 0) {
              ac.preDesc.unshift(news.pop());
            }
          } else if (conditional.fate === 'die' || conditional.fate === 'win') {
            ac.actions = [];
          }
          return false;
        }
      } else {
        if ('else' in conditional) {
          ac.postDesc.push(conditional.else);
        }
      }
    }

    return true;
  }

  private isMet(condRef: string): boolean {
    if (!condRef) {
      return true;   // say '' for true (unconditional means always met)
    }

    // condition that the hero possesses the item specified by its real_name
    for (const item of this.inventory) {
      if (item.real_name === condRef) {
        return true;
      }
    }

    // condition that the hero ever visited the specified place
    if (condRef in this.placeStates) {
      return true;
    }

    // a condition that is defined in the conditions array of the adventure data
    const condObj = this.adventureData.conditions.find(co => co.name === condRef);
    if (condObj) {
      const a = condObj.operands[0];
      const b = condObj.operands[1];

      switch (condObj.operator) {
        case 'not':
          return !this.isMet(a);
        case 'and': {
          let resultAnd = this.isMet(a);
          for (let i = 1; i < condObj.operands.length; i++) {
            if (!resultAnd) {
              return false;
            }
            resultAnd = resultAnd && this.isMet(condObj.operands[i]);
          }
          return resultAnd;
        }
        case 'or':
          let resultOr = this.isMet(a);
          for (let i = 1; i < condObj.operands.length; i++) {
            if (resultOr) {
              return true;
            }
            resultOr = resultOr || this.isMet(condObj.operands[i]);
          }
          return resultOr;
        case '<':
          return this.getValue(a) < this.getValue(b);
        case '>':
          return this.getValue(a) > this.getValue(b);
        case '=':
          return this.getValue(a) === this.getValue(b);
      }
    }

    return false;
  }

  private getValue(varRef: string): number {
    const matches = varRef.match(/^d([1-9][0-9]*)$/);
    if (matches) {
      return Math.floor(+matches[1] * Math.random());
    }

    if (isNaN(+varRef)) {
      return this.counters.get(varRef);
    }

    return +varRef;
  }
}
